import React, { useState, useEffect, useRef, useCallback } from 'react';
import { VscAdd, VscTrash, VscEdit, VscCheck, VscClose, VscChevronUp, VscChevronDown } from 'react-icons/vsc';
import PrintButton from '../common/PrintButton';
import { getTasks, createTask, updateTask, deleteTask } from '../../api/gantt';
import { buildGanttDiagramPdf } from '../../utils/pdfUtils';
import './Gantt.css';

const ZOOM = {
  week:  { pxPerDay: 18, label: 'Week' },
  month: { pxPerDay:  6, label: 'Month' },
};
const ROW_H    = 40;
const HDR_H    = 62;   // two-row header
const HDR_TOP  = 22;   // height of top (month) row
const LEFT_W   = 442;  // 160+86+86+38+72 (no assignee col)
const BAR_PAD  = 6;

const COLORS = [
  '#0e639c','#2ecc71','#e74c3c','#9b59b6',
  '#e67e22','#1abc9c','#e91e63','#f39c12',
];

function daysBetween(a, b) {
  return Math.round((new Date(b) - new Date(a)) / 86400000);
}
function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}
function isoDate(d) {
  return new Date(d).toISOString().slice(0, 10);
}
function fmtDate(d) {
  return new Date(d).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: '2-digit' });
}

function buildTimeRange(tasks) {
  if (!tasks.length) {
    const today = new Date();
    today.setDate(1);
    return { start: today, end: addDays(today, 90) };
  }
  let minD = new Date(tasks[0].startDate);
  let maxD = new Date(tasks[0].endDate);
  tasks.forEach(t => {
    const s = new Date(t.startDate), e = new Date(t.endDate);
    if (s < minD) minD = s;
    if (e > maxD) maxD = e;
  });
  minD = addDays(minD, -7);
  maxD = addDays(maxD,  14);
  return { start: minD, end: maxD };
}

function buildHeaderUnits(start, end, pxPerDay) {
  const units = [];
  const cur = new Date(start);
  cur.setDate(1);
  while (cur <= end) {
    const monthStart = new Date(cur);
    const nextMonth  = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
    const from = Math.max(0, daysBetween(start, monthStart));
    const to   = Math.min(daysBetween(start, end), daysBetween(start, nextMonth));
    if (to > from) {
      units.push({
        label: monthStart.toLocaleDateString(undefined, { month: 'short', year: 'numeric' }),
        x: from * pxPerDay,
        w: (to - from) * pxPerDay,
      });
    }
    cur.setMonth(cur.getMonth() + 1);
  }
  return units;
}

function buildWeekTicks(start, end, pxPerDay) {
  const ticks = [];
  const cur = new Date(start);
  // advance to next Monday
  const day = cur.getDay();
  if (day !== 1) cur.setDate(cur.getDate() + ((8 - day) % 7));
  while (cur <= end) {
    ticks.push(daysBetween(start, cur) * pxPerDay);
    cur.setDate(cur.getDate() + 7);
  }
  return ticks;
}

// Sub-units for week view: one entry per day
const DAY_SHORT = ['Su','Mo','Tu','We','Th','Fr','Sa'];
function buildDayUnits(start, end, pxPerDay) {
  const units = [];
  const cur = new Date(start);
  while (cur <= end) {
    const dow = cur.getDay();
    units.push({
      x:         daysBetween(start, cur) * pxPerDay,
      w:         pxPerDay,
      label:     String(cur.getDate()),
      dayName:   DAY_SHORT[dow],
      isWeekend: dow === 0 || dow === 6,
      isMonday:  dow === 1,
    });
    cur.setDate(cur.getDate() + 1);
  }
  return units;
}

// Sub-units for month view: one entry per week (Monday-based)
function buildWeekUnits(start, end, pxPerDay) {
  const units = [];
  const cur = new Date(start);
  while (cur.getDay() !== 1) cur.setDate(cur.getDate() + 1); // align to Monday
  while (cur <= end) {
    units.push({
      x:     daysBetween(start, cur) * pxPerDay,
      w:     7 * pxPerDay,
      label: cur.toLocaleDateString(undefined, { day: 'numeric', month: 'short' }),
    });
    cur.setDate(cur.getDate() + 7);
  }
  return units;
}

function emptyForm(projectId) {
  const today = isoDate(new Date());
  const end   = isoDate(addDays(new Date(), 14));
  return { name: '', startDate: today, endDate: end, color: COLORS[0], progress: 0, dependencies: [] };
}

function GanttView({ diagram, project, role, onOpenTab }) {
  const [tasks,    setTasks]    = useState([]);
  const [zoom,     setZoom]     = useState('week');
  const [editId,   setEditId]   = useState(null);
  const [draft,    setDraft]    = useState(null);
  const [newRow,   setNewRow]   = useState(null);
  const [error,    setError]    = useState('');
  const scrollRef  = useRef(null);
  const dragRef    = useRef(null); // { startX, startScrollLeft }

  const canEdit  = ['admin', 'engineer', 'manager', 'foreman'].includes(role);
  const canPrint = ['admin', 'engineer', 'manager', 'foreman', 'team-leader'].includes(role);
  const pxPerDay = ZOOM[zoom].pxPerDay;

  useEffect(() => {
    if (!diagram?._id) return;
    getTasks(diagram._id).then(r => setTasks(r.data)).catch(() => {});
  }, [diagram?._id]);

  const { start: tlStart, end: tlEnd } = buildTimeRange(tasks);
  const totalDays   = daysBetween(tlStart, tlEnd);
  const tlWidth     = totalDays * pxPerDay;
  const headerUnits = buildHeaderUnits(tlStart, tlEnd, pxPerDay);
  const subUnits    = zoom === 'week'
    ? buildDayUnits(tlStart, tlEnd, pxPerDay)
    : buildWeekUnits(tlStart, tlEnd, pxPerDay);
  const totalHeight = tasks.length * ROW_H;

  const taskX = (t) => Math.max(0, daysBetween(tlStart, t.startDate) * pxPerDay);
  const taskW = (t) => Math.max(4, daysBetween(t.startDate, t.endDate) * pxPerDay);

  const startEdit = (task) => {
    setEditId(task._id);
    setDraft({
      name:         task.name,
      startDate:    isoDate(task.startDate),
      endDate:      isoDate(task.endDate),
      color:        task.color || COLORS[0],
      progress:     task.progress ?? 0,
      dependencies: (task.dependencies || []).map(d => d._id || d),
    });
    setError('');
  };

  const commitEdit = async () => {
    if (!draft.name.trim()) return setError('Name is required.');
    try {
      const payload = { ...draft };
      const res = await updateTask(editId, payload);
      setTasks(prev => prev.map(t => t._id === editId ? res.data : t));
      setEditId(null); setDraft(null); setError('');
    } catch (e) {
      setError(e.response?.data?.msg || 'Save failed.');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this task?')) return;
    try {
      await deleteTask(id);
      setTasks(prev => prev.filter(t => t._id !== id));
    } catch {}
  };

  const moveRow = async (idx, dir) => {
    const next = [...tasks];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    // Update order fields
    const updates = next.map((t, i) => updateTask(t._id, { order: i }));
    setTasks(next);
    await Promise.all(updates).catch(() => {});
  };

  const saveNew = async () => {
    if (!newRow.name.trim()) return setError('Name is required.');
    try {
      const res = await createTask({ ...newRow, diagram: diagram._id, order: tasks.length });
      setTasks(prev => [...prev, res.data]);
      setNewRow(null); setError('');
    } catch (e) {
      setError(e.response?.data?.msg || 'Save failed.');
    }
  };

  const buildChartPdf = async (colKeys) => {
    const { start, end } = buildTimeRange(tasks);
    return buildGanttDiagramPdf(tasks, start, end, `Gantt — ${diagram.name}`, colKeys);
  };

  // Build dependency arrows
  const arrows = [];
  tasks.forEach((task, bi) => {
    (task.dependencies || []).forEach(dep => {
      const depId = dep._id || dep;
      const ai = tasks.findIndex(t => t._id === depId);
      if (ai < 0) return;
      const fromTask = tasks[ai];
      const x1 = taskX(fromTask) + taskW(fromTask);
      const y1 = ai * ROW_H + ROW_H / 2;
      const x2 = taskX(task);
      const y2 = bi * ROW_H + ROW_H / 2;
      arrows.push({ x1, y1, x2, y2, key: `${fromTask._id}-${task._id}` });
    });
  });

  const setF = (k) => (v) => setDraft(d => ({ ...d, [k]: v }));
  const setNF = (k) => (v) => setNewRow(d => ({ ...d, [k]: v }));

  const taskOpts = tasks.map(t => ({ value: t._id, label: t.name }));

  function renderEditFields(d, set, otherTaskId) {
    return (
      <>
        <input className="gantt-input" value={d.name} placeholder="Task name *"
          onChange={e => set('name')(e.target.value)} autoFocus />
        <div className="gantt-edit-row">
          <label>Start<input type="date" className="gantt-input" value={d.startDate} onChange={e => set('startDate')(e.target.value)} /></label>
          <label>End<input type="date" className="gantt-input" value={d.endDate} onChange={e => set('endDate')(e.target.value)} /></label>
          <label>Progress %
            <input type="number" className="gantt-input" min={0} max={100} value={d.progress}
              onChange={e => set('progress')(Number(e.target.value))} style={{ width: 60 }} />
          </label>
          <label>Colour
            <div className="gantt-color-row">
              {COLORS.map(c => (
                <button key={c} className={`gantt-color-btn${d.color === c ? ' active' : ''}`}
                  style={{ background: c }} onClick={() => set('color')(c)} type="button" />
              ))}
            </div>
          </label>
        </div>
        <label>Dependencies
          <div className="gantt-dep-list">
            {taskOpts.filter(t => t.value !== otherTaskId).map(t => (
              <label key={t.value} className="gantt-dep-item">
                <input type="checkbox"
                  checked={(d.dependencies || []).includes(t.value)}
                  onChange={e => {
                    const deps = d.dependencies || [];
                    set('dependencies')(e.target.checked ? [...deps, t.value] : deps.filter(x => x !== t.value));
                  }} />
                {t.label}
              </label>
            ))}
            {taskOpts.length === 0 && <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>No other tasks yet.</span>}
          </div>
        </label>
      </>
    );
  }

  if (!diagram) return <div className="empty-state">Select a diagram.</div>;

  return (
    <div className="gantt-wrap module-wrap">
      <div className="module-toolbar">
        <span className="module-toolbar-title">Gantt — {diagram.name}</span>
        <div className="gantt-zoom-btns">
          {Object.entries(ZOOM).map(([k, v]) => (
            <button key={k} className={`gantt-zoom-btn${zoom === k ? ' active' : ''}`} onClick={() => setZoom(k)}>
              {v.label}
            </button>
          ))}
        </div>
        {canPrint && <PrintButton
          title={`${diagram.name}${project ? ` — ${project.name}` : ''}`}
          storageKey="gantt"
          rows={tasks}
          onOpenTab={onOpenTab}
          chartPdf={tasks.length ? buildChartPdf : undefined}
          columns={[
            { key: 'name',     label: 'Task name',  render: t => t.name || '' },
            { key: 'start',    label: 'Start date', render: t => fmtDate(t.startDate) },
            { key: 'end',      label: 'End date',   render: t => fmtDate(t.endDate) },
            { key: 'progress', label: 'Progress %', render: t => `${t.progress ?? 0}%` },
          ]}
        />}
        {canEdit && (
          <button className="btn-primary" onClick={() => { setNewRow(emptyForm(project._id)); setError(''); }}>
            <VscAdd size={13} style={{ marginRight: 4 }} />Add Task
          </button>
        )}
      </div>

      {error && <div className="row-error">{error}</div>}

      {/* New task form */}
      {newRow && (
        <div className="gantt-new-form">
          <div className="gantt-new-form-title">New Task</div>
          {renderEditFields(newRow, setNF, null)}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button className="btn-primary" onClick={saveNew}>Add</button>
            <button className="btn-secondary" onClick={() => { setNewRow(null); setError(''); }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Edit form */}
      {editId && draft && (
        <div className="gantt-new-form">
          <div className="gantt-new-form-title">Edit Task</div>
          {renderEditFields(draft, setF, editId)}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button className="btn-primary" onClick={commitEdit}>Save</button>
            <button className="btn-secondary" onClick={() => { setEditId(null); setDraft(null); setError(''); }}>Cancel</button>
          </div>
        </div>
      )}

      {tasks.length === 0 && !newRow && !editId && (
        <div className="empty-state" style={{ paddingTop: 60 }}>
          <span>No tasks yet.{canEdit ? ' Click "Add Task" to get started.' : ''}</span>
        </div>
      )}

      {tasks.length > 0 && (
        <div
          className="gantt-body"
          ref={scrollRef}
          onMouseDown={e => {
            if (e.button !== 0) return;
            if (e.target.closest('.gantt-bar, .gantt-row-left, input, button, select')) return;
            dragRef.current = { startX: e.clientX, startScrollLeft: scrollRef.current.scrollLeft };
            scrollRef.current.style.cursor = 'grabbing';
            scrollRef.current.style.userSelect = 'none';
          }}
          onMouseMove={e => {
            if (!dragRef.current) return;
            const dx = e.clientX - dragRef.current.startX;
            scrollRef.current.scrollLeft = dragRef.current.startScrollLeft - dx;
          }}
          onMouseUp={() => {
            dragRef.current = null;
            if (scrollRef.current) { scrollRef.current.style.cursor = ''; scrollRef.current.style.userSelect = ''; }
          }}
          onMouseLeave={() => {
            dragRef.current = null;
            if (scrollRef.current) { scrollRef.current.style.cursor = ''; scrollRef.current.style.userSelect = ''; }
          }}
        >
          {/* Left fixed panel + scrollable timeline as one scrollable area */}
          <div className="gantt-scroll-area" style={{ minWidth: LEFT_W + tlWidth }}>

            {/* Header row */}
            <div className="gantt-hdr-row" style={{ height: HDR_H }}>
              <div className="gantt-left-hdr" style={{ width: LEFT_W }}>
                <span>Task</span>
                <span>Start</span>
                <span>End</span>
                <span>%</span>
                <span></span>
              </div>
              <div className="gantt-timeline-hdr" style={{ width: tlWidth }}>
                {/* Top row: month labels */}
                <div className="gantt-hdr-months" style={{ height: HDR_TOP }}>
                  {headerUnits.map((u, i) => (
                    <div key={i} className="gantt-month-label" style={{ left: u.x, width: u.w }}>
                      {u.label}
                    </div>
                  ))}
                </div>
                {/* Bottom row: day (week) or week (month) sub-labels */}
                <div className="gantt-hdr-subs" style={{ height: HDR_H - HDR_TOP }}>
                  {subUnits.map((u, i) => (
                    <div key={i}
                      className={`gantt-sub-label${u.isWeekend ? ' gantt-sub-we' : ''}${u.isMonday ? ' gantt-sub-mon' : ''}`}
                      style={{ left: u.x, width: u.w }}
                    >
                      {u.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Task rows */}
            {tasks.map((task, idx) => {
              const bx = taskX(task);
              const bw = taskW(task);
              return (
                <div key={task._id} className="gantt-row" style={{ height: ROW_H }}>
                  {/* Left cell */}
                  <div className="gantt-left-cell" style={{ width: LEFT_W }}>
                    <div className="gantt-task-name" title={task.name}>{task.name}</div>
                    <div className="gantt-task-date">{fmtDate(task.startDate)}</div>
                    <div className="gantt-task-date">{fmtDate(task.endDate)}</div>
                    <div className="gantt-task-pct">{task.progress ?? 0}%</div>
                    {canEdit && (
                      <div className="gantt-row-actions">
                        <button className="btn-icon" onClick={() => startEdit(task)} title="Edit"><VscEdit size={12} /></button>
                        <button className="btn-icon" onClick={() => handleDelete(task._id)} title="Delete"><VscTrash size={12} /></button>
                        <button className="btn-icon" onClick={() => moveRow(idx, -1)} disabled={idx === 0} title="Move up"><VscChevronUp size={12} /></button>
                        <button className="btn-icon" onClick={() => moveRow(idx, 1)} disabled={idx === tasks.length - 1} title="Move down"><VscChevronDown size={12} /></button>
                      </div>
                    )}
                  </div>

                  {/* Timeline cell */}
                  <div className="gantt-timeline-cell" style={{ width: tlWidth }}>
                    {/* Alternating row background */}
                    <div className={`gantt-row-bg${idx % 2 ? ' gantt-row-bg-alt' : ''}`} style={{ width: tlWidth }} />
                    {/* Day / week grid lines + weekend shading */}
                    {subUnits.map((u, si) => u.isWeekend
                      ? <div key={si} className="gantt-weekend-bg" style={{ left: u.x, width: u.w, height: ROW_H }} />
                      : <div key={si} className={`gantt-sub-tick${u.isMonday ? ' gantt-sub-tick-mon' : ''}`} style={{ left: u.x, height: ROW_H }} />
                    )}
                    {/* Bar */}
                    <div
                      className="gantt-bar"
                      style={{
                        left:             bx + BAR_PAD,
                        width:            Math.max(bw - BAR_PAD * 2, 4),
                        top:              8,
                        height:           ROW_H - 16,
                        background:       task.color || COLORS[0],
                      }}
                      title={`${task.name} — ${fmtDate(task.startDate)} → ${fmtDate(task.endDate)}`}
                    >
                      {/* Progress fill */}
                      {task.progress > 0 && (
                        <div className="gantt-bar-progress"
                          style={{ width: `${task.progress}%` }} />
                      )}
                      <span className="gantt-bar-label">{task.name}</span>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Today line */}
            {(() => {
              const todayX = daysBetween(tlStart, new Date()) * pxPerDay;
              if (todayX < 0 || todayX > tlWidth) return null;
              return (
                <div className="gantt-today-line" style={{ left: LEFT_W + todayX, top: 0, height: HDR_H + totalHeight }} />
              );
            })()}

            {/* SVG dependency arrows (absolute over timeline area) */}
            {arrows.length > 0 && (
              <svg
                className="gantt-arrows-svg"
                style={{ left: LEFT_W, top: HDR_H, width: tlWidth, height: totalHeight }}
              >
                <defs>
                  <marker id="arrowhead" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
                    <path className="gantt-arrowhead" d="M0,0 L0,6 L6,3 z" />
                  </marker>
                </defs>
                {arrows.map(a => {
                  const cx = (a.x1 + a.x2) / 2;
                  return (
                    <path key={a.key}
                      d={`M${a.x1},${a.y1} C${cx},${a.y1} ${cx},${a.y2} ${a.x2},${a.y2}`}
                      className="gantt-arrow"
                      markerEnd="url(#arrowhead)"
                    />
                  );
                })}
              </svg>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default GanttView;
